import { forwardRef, ReactElement, Ref } from 'react';
import cn from 'classnames';

import './text.scss';

export type TextAs = 'p' | 'span';

export type TextSize = 'tiny' | 'small' | 'medium' | 'large' | 'extra-large';

export type TextWeight = 'normal' | 'semibold' | 'bold';

export type TextColor = 'N1000' | 'N800' | 'N700' | 'N500' | 'N300' | 'N100' | 'N00';

export interface TextProps extends Omit<React.HTMLProps<HTMLParagraphElement>, 'css'> {
  /**
   * Render a span or a paragraph. Default 'span'.
   */
  as?: TextAs;
  /**
   * Text value to be rendered
   */
  text?: string;
  /**
   * Defines the text height
   */
  textHeight?: string;
  /**
   * Size of the text. Defaults to `medium`.
   */
  textSize?: TextSize;
  /**
   * Weight of the text
   */
  textWeight?: TextWeight;
  /**
   * Sets the text style to italic.
   */
  italic?: boolean;
  /**
   * Add a strikethrough to the text.
   */
  strike?: boolean;
  /**
   * Text value to be rendered
   */
  children?: string | number | ReactElement;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * Modifies the text as separator
   */
  separator?: boolean;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLParagraphElement>;
}

const TextComponent = (
  {
    as,
    children,
    italic,
    separator,
    strike,
    text,
    textHeight,
    textSize = 'medium',
    textWeight = 'normal',
    classNames = [],
    ...props
  }: TextProps,
  ref: TextProps['ref'],
) => {
  const classText = 'lsux-text';
  const setHeight = { height: textHeight, overflow: 'auto' };
  const useHeight = textHeight ? setHeight : {};

  const cnText = cn(
    {
      [`${classText}`]: true,
      [`${classText}--separator`]: separator,
      [`${classText}--italic`]: italic,
      [`${classText}--strike`]: strike,
      [`${classText}--${textSize}`]: textSize,
      [`${classText}--${textWeight}`]: textWeight,
    },
    classNames,
  );

  if (as === 'span') {
    return (
      <span {...props} ref={ref} style={{ ...useHeight, ...props.style }} className={cnText}>
        {text ? text : children}
      </span>
    );
  } else {
    return (
      <p {...props} ref={ref} style={{ ...useHeight, ...props.style }} className={cnText}>
        {text ? text : children}
      </p>
    );
  }
};

export const Text = forwardRef<HTMLParagraphElement, TextProps>(TextComponent);
